/**
 * 🏰 DUNGEON SYSTEM - CONSOLIDATED EXPORTS
 *
 * This file aggregates ALL dungeon-related data in one place:
 * - Enemy mobs and bosses (mobs.js)
 * - Equipment drops (equipment/ folder - 11 slot-type files)
 * - Consumable items (items/consumables.js)
 * - Crafting materials (items/mobDrops.js)
 * - Dungeon configuration and loot tables
 *
 * MODULAR STRUCTURE:
 * - equipment/     → weapons, armor, helmets, boots, gloves, bottoms,
 *                    capes, belts, offhands, pendants, rings
 * - items/         → consumables.js (potions, food, buffs, cubes, gemstones)
 *                  → mobDrops.js (crafting materials by rarity)
 *
 * STATE STORAGE: All collected items are stored in:
 * - GameContext.playerCharacter.inventory (unified inventory system)
 *
 * UI DISPLAY:
 * - ResourcePanel → Dungeon Tab: Shows materials from inventory
 * - PlayerPanel → Misc Tab: Shows all items from inventory
 */

// ============================================================
// ENEMIES & COMBAT
// ============================================================
import { ENEMY_TEMPLATES, DUNGEON_MOB_POOLS, DUNGEON_BOSS_POOLS } from './mobs';

// Dungeon tile types
export const TILE_TYPES = {
  EMPTY: 'empty',           // Safe tile
  COMBAT: 'combat',         // Enemy encounter
  TREASURE: 'treasure',     // Instant loot
  TRAP: 'trap',             // Damage/debuff
  REST: 'rest',             // Heal HP
  BOSS: 'boss',             // Boss fight (required for completion)
  ENTRANCE: 'entrance',     // Starting tile
  // Tower-specific tiles
  STAIRS_DOWN: 'stairsDown', // Go to next floor
  STAIRS_UP: 'stairsUp',     // Return to previous floor
  CHECKPOINT: 'checkpoint',  // Rest/exit point
};

// Dungeon configuration
export const DUNGEON_CONFIG = {
  // Map size
  GRID_SIZE: 9, // 9x9 hex grid

  // Tile spawn rates (higher = more common)
  TILE_WEIGHTS: {
    [TILE_TYPES.EMPTY]: 30,
    [TILE_TYPES.COMBAT]: 35,
    [TILE_TYPES.TREASURE]: 15,
    [TILE_TYPES.TRAP]: 10,
    [TILE_TYPES.REST]: 10,
  },

  // Tower-specific tile weights (harder: more combat, less healing/treasure)
  TOWER_TILE_WEIGHTS: {
    [TILE_TYPES.EMPTY]: 25,     // Slightly less empty
    [TILE_TYPES.COMBAT]: 45,    // More combat (was 35)
    [TILE_TYPES.TREASURE]: 10,  // Less treasure (was 15)
    [TILE_TYPES.TRAP]: 15,      // More traps (was 10)
    [TILE_TYPES.REST]: 5,       // Much less rest tiles (was 10)
  },

  // Fog of war
  FOG_ENABLED: true,
  VISION_RANGE: 1, // Reveal adjacent tiles

  // Dungeon tiers (unlocked progressively by Era)
  // Each tier has 4 level-based variants (every 5 levels within tier range)
  // dungeonType field links to specific dungeon biome (for mob/boss pools)
  TIERS: [
    // ═══════════════════════════════════════════════════════════════════════════
    // TIER 1A - ANCIENT RUINS (Levels 5-20, Era 1+) - General undead theme
    // ═══════════════════════════════════════════════════════════════════════════
    {
      id: 'ancientRuins_lv5',
      dungeonType: 'ancientRuins',
      name: 'Forgotten Crypt',
      icon: '🏚️',
      tier: 1,
      minEra: 1,
      minLevel: 5,
      baseEnemyLevel: 5,
      maxFloors: 1,
      lootTable: 'common',
      expReward: 80,
    },
    {
      id: 'ancientRuins_lv10',
      dungeonType: 'ancientRuins',
      name: 'Crumbling Tomb',
      icon: '🏚️',
      tier: 1,
      minEra: 1,
      minLevel: 10,
      baseEnemyLevel: 10,
      maxFloors: 1,
      lootTable: 'common',
      expReward: 120,
    },
    {
      id: 'ancientRuins_lv15',
      dungeonType: 'ancientRuins',
      name: 'Sunken Vault',
      icon: '🏚️',
      tier: 1,
      minEra: 1,
      minLevel: 15,
      baseEnemyLevel: 15,
      maxFloors: 1,
      lootTable: 'common',
      expReward: 180,
    },
    {
      id: 'ancientRuins_lv20',
      dungeonType: 'ancientRuins',
      name: 'Ancient Ruins',
      icon: '🏚️',
      tier: 1,
      minEra: 1,
      minLevel: 20,
      baseEnemyLevel: 20,
      maxFloors: 1,
      lootTable: 'uncommon',
      expReward: 250,
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // TIER 1B - GOBLIN CAVE (Levels 5-20, Era 1+) - Goblin-focused theme
    // ═══════════════════════════════════════════════════════════════════════════
    {
      id: 'goblinCave_lv5',
      dungeonType: 'goblinCave',
      name: 'Goblin Warren',
      icon: '🕳️',
      tier: 1,
      minEra: 1,
      minLevel: 5,
      baseEnemyLevel: 5,
      maxFloors: 1,
      lootTable: 'common',
      expReward: 80,
    },
    {
      id: 'goblinCave_lv10',
      dungeonType: 'goblinCave',
      name: 'Goblin Tunnels',
      icon: '🕳️',
      tier: 1,
      minEra: 1,
      minLevel: 10,
      baseEnemyLevel: 10,
      maxFloors: 1,
      lootTable: 'common',
      expReward: 120,
    },
    {
      id: 'goblinCave_lv15',
      dungeonType: 'goblinCave',
      name: 'Goblin Den',
      icon: '🕳️',
      tier: 1,
      minEra: 1,
      minLevel: 15,
      baseEnemyLevel: 15,
      maxFloors: 1,
      lootTable: 'common',
      expReward: 180,
    },
    {
      id: 'goblinCave_lv20',
      dungeonType: 'goblinCave',
      name: 'Goblin Stronghold',
      icon: '🕳️',
      tier: 1,
      minEra: 1,
      minLevel: 20,
      baseEnemyLevel: 20,
      maxFloors: 1,
      lootTable: 'uncommon',
      expReward: 250,
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // TIER 1C - BANDIT HIDEOUT (Levels 5-20, Era 1+) - Human outlaw theme
    // ═══════════════════════════════════════════════════════════════════════════
    {
      id: 'banditHideout_lv5',
      dungeonType: 'banditHideout',
      name: 'Outlaw Camp',
      icon: '⚔️',
      tier: 1,
      minEra: 1,
      minLevel: 5,
      baseEnemyLevel: 5,
      maxFloors: 1,
      lootTable: 'common',
      expReward: 80,
    },
    {
      id: 'banditHideout_lv10',
      dungeonType: 'banditHideout',
      name: 'Bandit Outpost',
      icon: '⚔️',
      tier: 1,
      minEra: 1,
      minLevel: 10,
      baseEnemyLevel: 10,
      maxFloors: 1,
      lootTable: 'common',
      expReward: 120,
    },
    {
      id: 'banditHideout_lv15',
      dungeonType: 'banditHideout',
      name: 'Bandit Fortress',
      icon: '⚔️',
      tier: 1,
      minEra: 1,
      minLevel: 15,
      baseEnemyLevel: 15,
      maxFloors: 1,
      lootTable: 'common',
      expReward: 180,
    },
    {
      id: 'banditHideout_lv20',
      dungeonType: 'banditHideout',
      name: 'Bandit Keep',
      icon: '⚔️',
      tier: 1,
      minEra: 1,
      minLevel: 20,
      baseEnemyLevel: 20,
      maxFloors: 1,
      lootTable: 'uncommon',
      expReward: 250,
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // TIER 1D - HAUNTED MINE (Levels 5-20, Era 1+) - Undead miners theme
    // ═══════════════════════════════════════════════════════════════════════════
    {
      id: 'hauntedMine_lv5',
      dungeonType: 'hauntedMine',
      name: 'Abandoned Shaft',
      icon: '⛏️',
      tier: 1,
      minEra: 1,
      minLevel: 5,
      baseEnemyLevel: 5,
      maxFloors: 1,
      lootTable: 'common',
      expReward: 80,
    },
    {
      id: 'hauntedMine_lv10',
      dungeonType: 'hauntedMine',
      name: 'Dark Tunnels',
      icon: '⛏️',
      tier: 1,
      minEra: 1,
      minLevel: 10,
      baseEnemyLevel: 10,
      maxFloors: 1,
      lootTable: 'common',
      expReward: 120,
    },
    {
      id: 'hauntedMine_lv15',
      dungeonType: 'hauntedMine',
      name: 'Collapsed Section',
      icon: '⛏️',
      tier: 1,
      minEra: 1,
      minLevel: 15,
      baseEnemyLevel: 15,
      maxFloors: 1,
      lootTable: 'common',
      expReward: 180,
    },
    {
      id: 'hauntedMine_lv20',
      dungeonType: 'hauntedMine',
      name: 'Cursed Depths',
      icon: '⛏️',
      tier: 1,
      minEra: 1,
      minLevel: 20,
      baseEnemyLevel: 20,
      maxFloors: 1,
      lootTable: 'uncommon',
      expReward: 250,
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // TIER 2A - DARK CATACOMBS (Levels 25-40, Era 3+) - Classic undead
    // ═══════════════════════════════════════════════════════════════════════════
    {
      id: 'darkCatacombs_lv25',
      dungeonType: 'darkCatacombs',
      name: 'Bone Pits',
      icon: '⚰️',
      tier: 2,
      minEra: 3,
      minLevel: 25,
      baseEnemyLevel: 25,
      maxFloors: 1,
      lootTable: 'uncommon',
      expReward: 350,
    },
    {
      id: 'darkCatacombs_lv30',
      dungeonType: 'darkCatacombs',
      name: 'Cursed Ossuary',
      icon: '⚰️',
      tier: 2,
      minEra: 3,
      minLevel: 30,
      baseEnemyLevel: 30,
      maxFloors: 1,
      lootTable: 'uncommon',
      expReward: 480,
    },
    {
      id: 'darkCatacombs_lv35',
      dungeonType: 'darkCatacombs',
      name: 'Necropolis',
      icon: '⚰️',
      tier: 2,
      minEra: 3,
      minLevel: 35,
      baseEnemyLevel: 35,
      maxFloors: 1,
      lootTable: 'rare',
      expReward: 650,
    },
    {
      id: 'darkCatacombs_lv40',
      dungeonType: 'darkCatacombs',
      name: 'Dark Catacombs',
      icon: '⚰️',
      tier: 2,
      minEra: 3,
      minLevel: 40,
      baseEnemyLevel: 40,
      maxFloors: 1,
      lootTable: 'rare',
      expReward: 850,
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // TIER 2B - FROZEN CRYPT (Levels 25-40, Era 3+) - Ice undead
    // ═══════════════════════════════════════════════════════════════════════════
    {
      id: 'frozenCrypt_lv25',
      dungeonType: 'frozenCrypt',
      name: 'Frost Barrow',
      icon: '❄️',
      tier: 2,
      minEra: 3,
      minLevel: 25,
      baseEnemyLevel: 25,
      maxFloors: 1,
      lootTable: 'uncommon',
      expReward: 350,
    },
    {
      id: 'frozenCrypt_lv30',
      dungeonType: 'frozenCrypt',
      name: 'Ice Tomb',
      icon: '❄️',
      tier: 2,
      minEra: 3,
      minLevel: 30,
      baseEnemyLevel: 30,
      maxFloors: 1,
      lootTable: 'uncommon',
      expReward: 480,
    },
    {
      id: 'frozenCrypt_lv35',
      dungeonType: 'frozenCrypt',
      name: 'Glacial Vault',
      icon: '❄️',
      tier: 2,
      minEra: 3,
      minLevel: 35,
      baseEnemyLevel: 35,
      maxFloors: 1,
      lootTable: 'rare',
      expReward: 650,
    },
    {
      id: 'frozenCrypt_lv40',
      dungeonType: 'frozenCrypt',
      name: 'Frozen Crypt',
      icon: '❄️',
      tier: 2,
      minEra: 3,
      minLevel: 40,
      baseEnemyLevel: 40,
      maxFloors: 1,
      lootTable: 'rare',
      expReward: 850,
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // TIER 2C - FLOODED SEWERS (Levels 25-40, Era 3+) - Plague/sewer
    // ═══════════════════════════════════════════════════════════════════════════
    {
      id: 'floodedSewers_lv25',
      dungeonType: 'floodedSewers',
      name: 'Rat Warrens',
      icon: '🐀',
      tier: 2,
      minEra: 3,
      minLevel: 25,
      baseEnemyLevel: 25,
      maxFloors: 1,
      lootTable: 'uncommon',
      expReward: 350,
    },
    {
      id: 'floodedSewers_lv30',
      dungeonType: 'floodedSewers',
      name: 'Toxic Drains',
      icon: '🐀',
      tier: 2,
      minEra: 3,
      minLevel: 30,
      baseEnemyLevel: 30,
      maxFloors: 1,
      lootTable: 'uncommon',
      expReward: 480,
    },
    {
      id: 'floodedSewers_lv35',
      dungeonType: 'floodedSewers',
      name: 'Plague Depths',
      icon: '🐀',
      tier: 2,
      minEra: 3,
      minLevel: 35,
      baseEnemyLevel: 35,
      maxFloors: 1,
      lootTable: 'rare',
      expReward: 650,
    },
    {
      id: 'floodedSewers_lv40',
      dungeonType: 'floodedSewers',
      name: 'Flooded Sewers',
      icon: '🐀',
      tier: 2,
      minEra: 3,
      minLevel: 40,
      baseEnemyLevel: 40,
      maxFloors: 1,
      lootTable: 'rare',
      expReward: 850,
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // TIER 2D - ORCISH STRONGHOLD (Levels 25-40, Era 3+) - Orc warriors
    // ═══════════════════════════════════════════════════════════════════════════
    {
      id: 'orcishStronghold_lv25',
      dungeonType: 'orcishStronghold',
      name: 'Orc Outpost',
      icon: '👹',
      tier: 2,
      minEra: 3,
      minLevel: 25,
      baseEnemyLevel: 25,
      maxFloors: 1,
      lootTable: 'uncommon',
      expReward: 350,
    },
    {
      id: 'orcishStronghold_lv30',
      dungeonType: 'orcishStronghold',
      name: 'War Camp',
      icon: '👹',
      tier: 2,
      minEra: 3,
      minLevel: 30,
      baseEnemyLevel: 30,
      maxFloors: 1,
      lootTable: 'uncommon',
      expReward: 480,
    },
    {
      id: 'orcishStronghold_lv35',
      dungeonType: 'orcishStronghold',
      name: 'Bloodfort',
      icon: '👹',
      tier: 2,
      minEra: 3,
      minLevel: 35,
      baseEnemyLevel: 35,
      maxFloors: 1,
      lootTable: 'rare',
      expReward: 650,
    },
    {
      id: 'orcishStronghold_lv40',
      dungeonType: 'orcishStronghold',
      name: 'Orcish Stronghold',
      icon: '👹',
      tier: 2,
      minEra: 3,
      minLevel: 40,
      baseEnemyLevel: 40,
      maxFloors: 1,
      lootTable: 'rare',
      expReward: 850,
    },

    // Legacy tier2_lv* IDs for backwards compatibility
    {
      id: 'tier2_lv25',
      dungeonType: 'darkCatacombs',
      name: 'Bone Pits',
      icon: '⚰️',
      tier: 2,
      minEra: 3,
      minLevel: 25,
      baseEnemyLevel: 25,
      maxFloors: 1,
      lootTable: 'uncommon',
      expReward: 350,
    },
    {
      id: 'tier2_lv30',
      dungeonType: 'darkCatacombs',
      name: 'Cursed Ossuary',
      icon: '⚰️',
      tier: 2,
      minEra: 3,
      minLevel: 30,
      baseEnemyLevel: 30,
      maxFloors: 1,
      lootTable: 'uncommon',
      expReward: 480,
    },
    {
      id: 'tier2_lv35',
      dungeonType: 'darkCatacombs',
      name: 'Necropolis',
      icon: '⚰️',
      tier: 2,
      minEra: 3,
      minLevel: 35,
      baseEnemyLevel: 35,
      maxFloors: 1,
      lootTable: 'rare',
      expReward: 650,
    },
    {
      id: 'tier2_lv40',
      dungeonType: 'darkCatacombs',
      name: 'Dark Catacombs',
      icon: '⚰️',
      tier: 2,
      minEra: 3,
      minLevel: 40,
      baseEnemyLevel: 40,
      maxFloors: 1,
      lootTable: 'rare',
      expReward: 850,
    },
    // ═══════════════════════════════════════════════════════════════════════════
    // TIER 3A - CORRUPTED TEMPLE (Levels 45-60, Era 5+) - Fallen divine
    // ═══════════════════════════════════════════════════════════════════════════
    {
      id: 'corruptedTemple_lv45',
      dungeonType: 'corruptedTemple',
      name: 'Defiled Shrine',
      icon: '🛕',
      tier: 3,
      minEra: 5,
      minLevel: 45,
      baseEnemyLevel: 45,
      maxFloors: 1,
      lootTable: 'rare',
      expReward: 1100,
    },
    {
      id: 'corruptedTemple_lv50',
      dungeonType: 'corruptedTemple',
      name: 'Blighted Sanctum',
      icon: '🛕',
      tier: 3,
      minEra: 5,
      minLevel: 50,
      baseEnemyLevel: 50,
      maxFloors: 1,
      lootTable: 'rare',
      expReward: 1400,
    },
    {
      id: 'corruptedTemple_lv55',
      dungeonType: 'corruptedTemple',
      name: 'Tainted Cathedral',
      icon: '🛕',
      tier: 3,
      minEra: 5,
      minLevel: 55,
      baseEnemyLevel: 55,
      maxFloors: 1,
      lootTable: 'epic',
      expReward: 1800,
    },
    {
      id: 'corruptedTemple_lv60',
      dungeonType: 'corruptedTemple',
      name: 'Corrupted Temple',
      icon: '🛕',
      tier: 3,
      minEra: 5,
      minLevel: 60,
      baseEnemyLevel: 60,
      maxFloors: 1,
      lootTable: 'epic',
      expReward: 2300,
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // TIER 3B - DRAGON'S LAIR (Levels 45-60, Era 5+) - Dragon cult
    // ═══════════════════════════════════════════════════════════════════════════
    {
      id: 'dragonsLair_lv45',
      dungeonType: 'dragonsLair',
      name: 'Cult Cavern',
      icon: '🐉',
      tier: 3,
      minEra: 5,
      minLevel: 45,
      baseEnemyLevel: 45,
      maxFloors: 1,
      lootTable: 'rare',
      expReward: 1100,
    },
    {
      id: 'dragonsLair_lv50',
      dungeonType: 'dragonsLair',
      name: 'Drake Nesting',
      icon: '🐉',
      tier: 3,
      minEra: 5,
      minLevel: 50,
      baseEnemyLevel: 50,
      maxFloors: 1,
      lootTable: 'rare',
      expReward: 1400,
    },
    {
      id: 'dragonsLair_lv55',
      dungeonType: 'dragonsLair',
      name: 'Flame Sanctum',
      icon: '🐉',
      tier: 3,
      minEra: 5,
      minLevel: 55,
      baseEnemyLevel: 55,
      maxFloors: 1,
      lootTable: 'epic',
      expReward: 1800,
    },
    {
      id: 'dragonsLair_lv60',
      dungeonType: 'dragonsLair',
      name: "Dragon's Lair",
      icon: '🐉',
      tier: 3,
      minEra: 5,
      minLevel: 60,
      baseEnemyLevel: 60,
      maxFloors: 1,
      lootTable: 'epic',
      expReward: 2300,
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // TIER 3C - DEMONIC SANCTUM (Levels 45-60, Era 5+) - Pure demons
    // ═══════════════════════════════════════════════════════════════════════════
    {
      id: 'demonicSanctum_lv45',
      dungeonType: 'demonicSanctum',
      name: 'Fel Portal',
      icon: '👿',
      tier: 3,
      minEra: 5,
      minLevel: 45,
      baseEnemyLevel: 45,
      maxFloors: 1,
      lootTable: 'rare',
      expReward: 1100,
    },
    {
      id: 'demonicSanctum_lv50',
      dungeonType: 'demonicSanctum',
      name: 'Infernal Gate',
      icon: '👿',
      tier: 3,
      minEra: 5,
      minLevel: 50,
      baseEnemyLevel: 50,
      maxFloors: 1,
      lootTable: 'rare',
      expReward: 1400,
    },
    {
      id: 'demonicSanctum_lv55',
      dungeonType: 'demonicSanctum',
      name: 'Hellspawn Pit',
      icon: '👿',
      tier: 3,
      minEra: 5,
      minLevel: 55,
      baseEnemyLevel: 55,
      maxFloors: 1,
      lootTable: 'epic',
      expReward: 1800,
    },
    {
      id: 'demonicSanctum_lv60',
      dungeonType: 'demonicSanctum',
      name: 'Demonic Sanctum',
      icon: '👿',
      tier: 3,
      minEra: 5,
      minLevel: 60,
      baseEnemyLevel: 60,
      maxFloors: 1,
      lootTable: 'epic',
      expReward: 2300,
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // TIER 3D - WITCH'S COVEN (Levels 45-60, Era 5+) - Dark magic
    // ═══════════════════════════════════════════════════════════════════════════
    {
      id: 'witchsCoven_lv45',
      dungeonType: 'witchsCoven',
      name: 'Cursed Grove',
      icon: '🧙‍♀️',
      tier: 3,
      minEra: 5,
      minLevel: 45,
      baseEnemyLevel: 45,
      maxFloors: 1,
      lootTable: 'rare',
      expReward: 1100,
    },
    {
      id: 'witchsCoven_lv50',
      dungeonType: 'witchsCoven',
      name: 'Hex Circle',
      icon: '🧙‍♀️',
      tier: 3,
      minEra: 5,
      minLevel: 50,
      baseEnemyLevel: 50,
      maxFloors: 1,
      lootTable: 'rare',
      expReward: 1400,
    },
    {
      id: 'witchsCoven_lv55',
      dungeonType: 'witchsCoven',
      name: 'Dark Sabbath',
      icon: '🧙‍♀️',
      tier: 3,
      minEra: 5,
      minLevel: 55,
      baseEnemyLevel: 55,
      maxFloors: 1,
      lootTable: 'epic',
      expReward: 1800,
    },
    {
      id: 'witchsCoven_lv60',
      dungeonType: 'witchsCoven',
      name: "Witch's Coven",
      icon: '🧙‍♀️',
      tier: 3,
      minEra: 5,
      minLevel: 60,
      baseEnemyLevel: 60,
      maxFloors: 1,
      lootTable: 'epic',
      expReward: 2300,
    },

    // Legacy tier3_lv* IDs for backwards compatibility
    {
      id: 'tier3_lv45',
      dungeonType: 'corruptedTemple',
      name: 'Defiled Shrine',
      icon: '🛕',
      tier: 3,
      minEra: 5,
      minLevel: 45,
      baseEnemyLevel: 45,
      maxFloors: 1,
      lootTable: 'rare',
      expReward: 1100,
    },
    {
      id: 'tier3_lv50',
      dungeonType: 'corruptedTemple',
      name: 'Blighted Sanctum',
      icon: '🛕',
      tier: 3,
      minEra: 5,
      minLevel: 50,
      baseEnemyLevel: 50,
      maxFloors: 1,
      lootTable: 'rare',
      expReward: 1400,
    },
    {
      id: 'tier3_lv55',
      dungeonType: 'corruptedTemple',
      name: 'Tainted Cathedral',
      icon: '🛕',
      tier: 3,
      minEra: 5,
      minLevel: 55,
      baseEnemyLevel: 55,
      maxFloors: 1,
      lootTable: 'epic',
      expReward: 1800,
    },
    {
      id: 'tier3_lv60',
      dungeonType: 'corruptedTemple',
      name: 'Corrupted Temple',
      icon: '🛕',
      tier: 3,
      minEra: 5,
      minLevel: 60,
      baseEnemyLevel: 60,
      maxFloors: 1,
      lootTable: 'epic',
      expReward: 2300,
    },
    // ═══════════════════════════════════════════════════════════════════════════
    // TIER 4A - ABYSSAL DEPTHS (Levels 65-80, Era 7+) - Deep void
    // ═══════════════════════════════════════════════════════════════════════════
    {
      id: 'abyssalDepths_lv65',
      dungeonType: 'abyssalDepths',
      name: 'Shadow Chasm',
      icon: '🕳️',
      tier: 4,
      minEra: 7,
      minLevel: 65,
      baseEnemyLevel: 65,
      maxFloors: 1,
      lootTable: 'epic',
      expReward: 2900,
    },
    {
      id: 'abyssalDepths_lv70',
      dungeonType: 'abyssalDepths',
      name: 'Nightmare Grotto',
      icon: '🕳️',
      tier: 4,
      minEra: 7,
      minLevel: 70,
      baseEnemyLevel: 70,
      maxFloors: 1,
      lootTable: 'epic',
      expReward: 3600,
    },
    {
      id: 'abyssalDepths_lv75',
      dungeonType: 'abyssalDepths',
      name: 'Demon Pit',
      icon: '🕳️',
      tier: 4,
      minEra: 7,
      minLevel: 75,
      baseEnemyLevel: 75,
      maxFloors: 1,
      lootTable: 'legendary',
      expReward: 4500,
    },
    {
      id: 'abyssalDepths_lv80',
      dungeonType: 'abyssalDepths',
      name: 'Abyssal Depths',
      icon: '🕳️',
      tier: 4,
      minEra: 7,
      minLevel: 80,
      baseEnemyLevel: 80,
      maxFloors: 1,
      lootTable: 'legendary',
      expReward: 5600,
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // TIER 4B - INFERNAL FORTRESS (Levels 65-80, Era 7+) - Fire demons
    // ═══════════════════════════════════════════════════════════════════════════
    {
      id: 'infernalFortress_lv65',
      dungeonType: 'infernalFortress',
      name: 'Burning Gate',
      icon: '🔥',
      tier: 4,
      minEra: 7,
      minLevel: 65,
      baseEnemyLevel: 65,
      maxFloors: 1,
      lootTable: 'epic',
      expReward: 2900,
    },
    {
      id: 'infernalFortress_lv70',
      dungeonType: 'infernalFortress',
      name: 'Molten Halls',
      icon: '🔥',
      tier: 4,
      minEra: 7,
      minLevel: 70,
      baseEnemyLevel: 70,
      maxFloors: 1,
      lootTable: 'epic',
      expReward: 3600,
    },
    {
      id: 'infernalFortress_lv75',
      dungeonType: 'infernalFortress',
      name: 'Hellfire Core',
      icon: '🔥',
      tier: 4,
      minEra: 7,
      minLevel: 75,
      baseEnemyLevel: 75,
      maxFloors: 1,
      lootTable: 'legendary',
      expReward: 4500,
    },
    {
      id: 'infernalFortress_lv80',
      dungeonType: 'infernalFortress',
      name: 'Infernal Fortress',
      icon: '🔥',
      tier: 4,
      minEra: 7,
      minLevel: 80,
      baseEnemyLevel: 80,
      maxFloors: 1,
      lootTable: 'legendary',
      expReward: 5600,
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // TIER 4C - SHADOW REALM (Levels 65-80, Era 7+) - Nightmare/shadow
    // ═══════════════════════════════════════════════════════════════════════════
    {
      id: 'shadowRealm_lv65',
      dungeonType: 'shadowRealm',
      name: 'Dark Threshold',
      icon: '🌑',
      tier: 4,
      minEra: 7,
      minLevel: 65,
      baseEnemyLevel: 65,
      maxFloors: 1,
      lootTable: 'epic',
      expReward: 2900,
    },
    {
      id: 'shadowRealm_lv70',
      dungeonType: 'shadowRealm',
      name: 'Umbral Depths',
      icon: '🌑',
      tier: 4,
      minEra: 7,
      minLevel: 70,
      baseEnemyLevel: 70,
      maxFloors: 1,
      lootTable: 'epic',
      expReward: 3600,
    },
    {
      id: 'shadowRealm_lv75',
      dungeonType: 'shadowRealm',
      name: 'Nightmare Plane',
      icon: '🌑',
      tier: 4,
      minEra: 7,
      minLevel: 75,
      baseEnemyLevel: 75,
      maxFloors: 1,
      lootTable: 'legendary',
      expReward: 4500,
    },
    {
      id: 'shadowRealm_lv80',
      dungeonType: 'shadowRealm',
      name: 'Shadow Realm',
      icon: '🌑',
      tier: 4,
      minEra: 7,
      minLevel: 80,
      baseEnemyLevel: 80,
      maxFloors: 1,
      lootTable: 'legendary',
      expReward: 5600,
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // TIER 4D - ELDRITCH LIBRARY (Levels 65-80, Era 7+) - Forbidden knowledge
    // ═══════════════════════════════════════════════════════════════════════════
    {
      id: 'eldritchLibrary_lv65',
      dungeonType: 'eldritchLibrary',
      name: 'Forgotten Wing',
      icon: '📚',
      tier: 4,
      minEra: 7,
      minLevel: 65,
      baseEnemyLevel: 65,
      maxFloors: 1,
      lootTable: 'epic',
      expReward: 2900,
    },
    {
      id: 'eldritchLibrary_lv70',
      dungeonType: 'eldritchLibrary',
      name: 'Cursed Archives',
      icon: '📚',
      tier: 4,
      minEra: 7,
      minLevel: 70,
      baseEnemyLevel: 70,
      maxFloors: 1,
      lootTable: 'epic',
      expReward: 3600,
    },
    {
      id: 'eldritchLibrary_lv75',
      dungeonType: 'eldritchLibrary',
      name: 'Forbidden Section',
      icon: '📚',
      tier: 4,
      minEra: 7,
      minLevel: 75,
      baseEnemyLevel: 75,
      maxFloors: 1,
      lootTable: 'legendary',
      expReward: 4500,
    },
    {
      id: 'eldritchLibrary_lv80',
      dungeonType: 'eldritchLibrary',
      name: 'Eldritch Library',
      icon: '📚',
      tier: 4,
      minEra: 7,
      minLevel: 80,
      baseEnemyLevel: 80,
      maxFloors: 1,
      lootTable: 'legendary',
      expReward: 5600,
    },

    // Legacy tier4_lv* IDs for backwards compatibility
    {
      id: 'tier4_lv65',
      dungeonType: 'abyssalDepths',
      name: 'Shadow Chasm',
      icon: '🕳️',
      tier: 4,
      minEra: 7,
      minLevel: 65,
      baseEnemyLevel: 65,
      maxFloors: 1,
      lootTable: 'epic',
      expReward: 2900,
    },
    {
      id: 'tier4_lv70',
      dungeonType: 'abyssalDepths',
      name: 'Nightmare Grotto',
      icon: '🕳️',
      tier: 4,
      minEra: 7,
      minLevel: 70,
      baseEnemyLevel: 70,
      maxFloors: 1,
      lootTable: 'epic',
      expReward: 3600,
    },
    {
      id: 'tier4_lv75',
      dungeonType: 'abyssalDepths',
      name: 'Demon Pit',
      icon: '🕳️',
      tier: 4,
      minEra: 7,
      minLevel: 75,
      baseEnemyLevel: 75,
      maxFloors: 1,
      lootTable: 'legendary',
      expReward: 4500,
    },
    {
      id: 'tier4_lv80',
      dungeonType: 'abyssalDepths',
      name: 'Abyssal Depths',
      icon: '🕳️',
      tier: 4,
      minEra: 7,
      minLevel: 80,
      baseEnemyLevel: 80,
      maxFloors: 1,
      lootTable: 'legendary',
      expReward: 5600,
    },
    // ═══════════════════════════════════════════════════════════════════════════
    // TIER 5A - VOID CITADEL (Levels 85-100, Era 9+) - Pure void
    // ═══════════════════════════════════════════════════════════════════════════
    {
      id: 'voidCitadel_lv85',
      dungeonType: 'voidCitadel',
      name: 'Rift Nexus',
      icon: '🌌',
      tier: 5,
      minEra: 9,
      minLevel: 85,
      baseEnemyLevel: 85,
      maxFloors: 1,
      lootTable: 'legendary',
      expReward: 7000,
    },
    {
      id: 'voidCitadel_lv90',
      dungeonType: 'voidCitadel',
      name: 'Astral Prison',
      icon: '🌌',
      tier: 5,
      minEra: 9,
      minLevel: 90,
      baseEnemyLevel: 90,
      maxFloors: 1,
      lootTable: 'legendary',
      expReward: 8500,
    },
    {
      id: 'voidCitadel_lv95',
      dungeonType: 'voidCitadel',
      name: 'Cosmic Throne',
      icon: '🌌',
      tier: 5,
      minEra: 9,
      minLevel: 95,
      baseEnemyLevel: 95,
      maxFloors: 1,
      lootTable: 'mythic',
      expReward: 10500,
    },
    {
      id: 'voidCitadel_lv100',
      dungeonType: 'voidCitadel',
      name: 'Void Citadel',
      icon: '🌌',
      tier: 5,
      minEra: 9,
      minLevel: 100,
      baseEnemyLevel: 100,
      maxFloors: 1,
      lootTable: 'mythic',
      expReward: 13000,
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // TIER 5B - CELESTIAL RUINS (Levels 85-100, Era 9+) - Fallen divine
    // ═══════════════════════════════════════════════════════════════════════════
    {
      id: 'celestialRuins_lv85',
      dungeonType: 'celestialRuins',
      name: 'Fallen Spire',
      icon: '👼',
      tier: 5,
      minEra: 9,
      minLevel: 85,
      baseEnemyLevel: 85,
      maxFloors: 1,
      lootTable: 'legendary',
      expReward: 7000,
    },
    {
      id: 'celestialRuins_lv90',
      dungeonType: 'celestialRuins',
      name: 'Broken Heavens',
      icon: '👼',
      tier: 5,
      minEra: 9,
      minLevel: 90,
      baseEnemyLevel: 90,
      maxFloors: 1,
      lootTable: 'legendary',
      expReward: 8500,
    },
    {
      id: 'celestialRuins_lv95',
      dungeonType: 'celestialRuins',
      name: 'Divine Wasteland',
      icon: '👼',
      tier: 5,
      minEra: 9,
      minLevel: 95,
      baseEnemyLevel: 95,
      maxFloors: 1,
      lootTable: 'mythic',
      expReward: 10500,
    },
    {
      id: 'celestialRuins_lv100',
      dungeonType: 'celestialRuins',
      name: 'Celestial Ruins',
      icon: '👼',
      tier: 5,
      minEra: 9,
      minLevel: 100,
      baseEnemyLevel: 100,
      maxFloors: 1,
      lootTable: 'mythic',
      expReward: 13000,
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // TIER 5C - DIMENSIONAL RIFT (Levels 85-100, Era 9+) - Reality-bending
    // ═══════════════════════════════════════════════════════════════════════════
    {
      id: 'dimensionalRift_lv85',
      dungeonType: 'dimensionalRift',
      name: 'Reality Crack',
      icon: '💥',
      tier: 5,
      minEra: 9,
      minLevel: 85,
      baseEnemyLevel: 85,
      maxFloors: 1,
      lootTable: 'legendary',
      expReward: 7000,
    },
    {
      id: 'dimensionalRift_lv90',
      dungeonType: 'dimensionalRift',
      name: 'Planar Breach',
      icon: '💥',
      tier: 5,
      minEra: 9,
      minLevel: 90,
      baseEnemyLevel: 90,
      maxFloors: 1,
      lootTable: 'legendary',
      expReward: 8500,
    },
    {
      id: 'dimensionalRift_lv95',
      dungeonType: 'dimensionalRift',
      name: 'Temporal Wound',
      icon: '💥',
      tier: 5,
      minEra: 9,
      minLevel: 95,
      baseEnemyLevel: 95,
      maxFloors: 1,
      lootTable: 'mythic',
      expReward: 10500,
    },
    {
      id: 'dimensionalRift_lv100',
      dungeonType: 'dimensionalRift',
      name: 'Dimensional Rift',
      icon: '💥',
      tier: 5,
      minEra: 9,
      minLevel: 100,
      baseEnemyLevel: 100,
      maxFloors: 1,
      lootTable: 'mythic',
      expReward: 13000,
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // TIER 5D - CHAOS NEXUS (Levels 85-100, Era 9+) - Entropy/chaos
    // ═══════════════════════════════════════════════════════════════════════════
    {
      id: 'chaosNexus_lv85',
      dungeonType: 'chaosNexus',
      name: 'Entropy Scar',
      icon: '🌀',
      tier: 5,
      minEra: 9,
      minLevel: 85,
      baseEnemyLevel: 85,
      maxFloors: 1,
      lootTable: 'legendary',
      expReward: 7000,
    },
    {
      id: 'chaosNexus_lv90',
      dungeonType: 'chaosNexus',
      name: 'Madness Core',
      icon: '🌀',
      tier: 5,
      minEra: 9,
      minLevel: 90,
      baseEnemyLevel: 90,
      maxFloors: 1,
      lootTable: 'legendary',
      expReward: 8500,
    },
    {
      id: 'chaosNexus_lv95',
      dungeonType: 'chaosNexus',
      name: 'Primordial Vortex',
      icon: '🌀',
      tier: 5,
      minEra: 9,
      minLevel: 95,
      baseEnemyLevel: 95,
      maxFloors: 1,
      lootTable: 'mythic',
      expReward: 10500,
    },
    {
      id: 'chaosNexus_lv100',
      dungeonType: 'chaosNexus',
      name: 'Chaos Nexus',
      icon: '🌀',
      tier: 5,
      minEra: 9,
      minLevel: 100,
      baseEnemyLevel: 100,
      maxFloors: 1,
      lootTable: 'mythic',
      expReward: 13000,
    },

    // Legacy tier5_lv* IDs for backwards compatibility
    {
      id: 'tier5_lv85',
      dungeonType: 'voidCitadel',
      name: 'Rift Nexus',
      icon: '🌌',
      tier: 5,
      minEra: 9,
      minLevel: 85,
      baseEnemyLevel: 85,
      maxFloors: 1,
      lootTable: 'legendary',
      expReward: 7000,
    },
    {
      id: 'tier5_lv90',
      dungeonType: 'voidCitadel',
      name: 'Astral Prison',
      icon: '🌌',
      tier: 5,
      minEra: 9,
      minLevel: 90,
      baseEnemyLevel: 90,
      maxFloors: 1,
      lootTable: 'legendary',
      expReward: 8500,
    },
    {
      id: 'tier5_lv95',
      dungeonType: 'voidCitadel',
      name: 'Cosmic Throne',
      icon: '🌌',
      tier: 5,
      minEra: 9,
      minLevel: 95,
      baseEnemyLevel: 95,
      maxFloors: 1,
      lootTable: 'mythic',
      expReward: 10500,
    },
    {
      id: 'tier5_lv100',
      dungeonType: 'voidCitadel',
      name: 'Void Citadel',
      icon: '🌌',
      tier: 5,
      minEra: 9,
      minLevel: 100,
      baseEnemyLevel: 100,
      maxFloors: 1,
      lootTable: 'mythic',
      expReward: 13000,
    },
  ],

  // Legacy tier mapping for backward compatibility
  TIER_INFO: {
    tier1: { name: 'Ancient Ruins', icon: '🏚️', minEra: 1, levelRange: [5, 20] },
    tier2: { name: 'Dark Catacombs', icon: '⚰️', minEra: 3, levelRange: [25, 40] },
    tier3: { name: 'Corrupted Temple', icon: '🛕', minEra: 5, levelRange: [45, 60] },
    tier4: { name: 'Abyssal Depths', icon: '🕳️', minEra: 7, levelRange: [65, 80] },
    tier5: { name: 'Void Citadel', icon: '🌌', minEra: 9, levelRange: [85, 100] },
  },
};

// ============================================================
// ITEMS & LOOT
// ============================================================
// Re-export enemy templates and mob pools
export { ENEMY_TEMPLATES, DUNGEON_MOB_POOLS, DUNGEON_BOSS_POOLS } from './mobs';

// Re-export equipment (weapons, armor, accessories) - now from modular folder
export * from './equipment/index';

// Re-export consumables and materials - now from modular folder
// (items/index.js includes both consumables and mob drops/resources)
export * from './items/index';

// Explicit lootbox re-exports (webpack sometimes has issues with nested star exports)
export { LOOTBOXES, isLootbox, getLootbox, getChestForTier, getAllLootboxIds } from './items/lootboxes';

// ============================================================
// LOOT TABLES
// ============================================================
// Loot tables (rarity distribution) - Equipment rates reduced by 70%
export const LOOT_TABLES = {
  common: {
    equipment: 0.18,  // 18% chance for equipment (was 60%)
    gold: 0.52,       // Increased gold to compensate
    resources: 0.30,  // Increased resources to compensate
    rarityWeights: {
      common: 70,
      uncommon: 25,
      rare: 5,
      epic: 0,
      legendary: 0,
    },
  },

  uncommon: {
    equipment: 0.21,  // 21% (was 70%)
    gold: 0.44,
    resources: 0.35,
    rarityWeights: {
      common: 40,
      uncommon: 40,
      rare: 18,
      epic: 2,
      legendary: 0,
    },
  },

  rare: {
    equipment: 0.22,  // 22% (was 75%)
    gold: 0.38,
    resources: 0.40,
    rarityWeights: {
      common: 20,
      uncommon: 40,
      rare: 30,
      epic: 9,
      legendary: 1,
    },
  },

  epic: {
    equipment: 0.24,  // 24% (was 80%)
    gold: 0.36,
    resources: 0.40,
    rarityWeights: {
      common: 10,
      uncommon: 30,
      rare: 35,
      epic: 20,
      legendary: 5,
    },
  },

  legendary: {
    equipment: 0.26,  // 26% (was 85%)
    gold: 0.34,
    resources: 0.40,
    rarityWeights: {
      common: 0,
      uncommon: 10,
      rare: 25,
      epic: 35,
      legendary: 25,
      mythic: 5,
    },
  },

  mythic: {
    equipment: 0.27,  // 27% (was 90%)
    gold: 0.33,
    resources: 0.40,
    rarityWeights: {
      common: 0,
      uncommon: 5,
      rare: 15,
      epic: 30,
      legendary: 35,
      mythic: 15,
    },
  },

  boss: {
    equipment: 0.50,  // 50% (was 90%) - Bosses still have higher rates
    gold: 0.25,
    resources: 0.25,
    rarityWeights: {
      common: 0,
      uncommon: 10,
      rare: 25,
      epic: 35,
      legendary: 25,
      mythic: 5,
    },
    guaranteedDrops: 1, // Reduced from 2 to 1
  },
};

// Equipment stat ranges by rarity
export const EQUIPMENT_STATS = {
  weapon: {
    stats: ['attack', 'critChance', 'critDamage'],
    common: { attack: [2, 5], critChance: [0.01, 0.03] },
    uncommon: { attack: [5, 10], critChance: [0.03, 0.06], critDamage: [0.1, 0.2] },
    rare: { attack: [10, 18], critChance: [0.06, 0.10], critDamage: [0.2, 0.4] },
    epic: { attack: [18, 30], critChance: [0.10, 0.15], critDamage: [0.4, 0.7] },
    legendary: { attack: [30, 50], critChance: [0.15, 0.25], critDamage: [0.7, 1.2] },
    mythic: { attack: [80, 100], critChance: [0.28, 0.42], critDamage: [1.5, 2.2] },
  },

  armor: {
    stats: ['defense', 'health'],
    common: { defense: [1, 3], health: [10, 20] },
    uncommon: { defense: [3, 6], health: [20, 40] },
    rare: { defense: [6, 12], health: [40, 70] },
    epic: { defense: [12, 20], health: [70, 120] },
    legendary: { defense: [20, 35], health: [120, 200] },
    mythic: { defense: [35, 50], health: [250, 400] },
  },

  helmet: {
    stats: ['health', 'defense'],
    common: { health: [5, 15], defense: [1, 2] },
    uncommon: { health: [15, 30], defense: [2, 4] },
    rare: { health: [30, 50], defense: [4, 7] },
    epic: { health: [50, 80], defense: [7, 12] },
    legendary: { health: [80, 150], defense: [12, 20] },
    mythic: { health: [180, 280], defense: [22, 35] },
  },

  boots: {
    stats: ['speed', 'defense'],
    common: { speed: [0, 1], defense: [1, 2] },
    uncommon: { speed: [1, 1], defense: [2, 3] },
    rare: { speed: [1, 2], defense: [3, 5] },
    epic: { speed: [2, 3], defense: [5, 8] },
    legendary: { speed: [3, 4], defense: [8, 12] },
    mythic: { speed: [4, 5], defense: [15, 22] },
  },

  accessory: {
    stats: ['critChance', 'critDamage', 'health'],
    common: { critChance: [0.02, 0.04], health: [5, 10] },
    uncommon: { critChance: [0.04, 0.07], critDamage: [0.1, 0.2], health: [10, 20] },
    rare: { critChance: [0.07, 0.12], critDamage: [0.2, 0.4], health: [20, 35] },
    epic: { critChance: [0.12, 0.18], critDamage: [0.4, 0.7], health: [35, 60] },
    legendary: { critChance: [0.18, 0.30], critDamage: [0.7, 1.5], health: [60, 100] },
    mythic: { critChance: [0.30, 0.45], critDamage: [1.5, 2.5], health: [120, 180] },
  },
};

// Equipment icons by slot and rarity
export const EQUIPMENT_ICONS = {
  weapon: {
    common: ['🗡️', '⚔️', '🔪'],
    uncommon: ['🗡️', '⚔️', '🏹'],
    rare: ['⚔️', '🏹', '🔨'],
    epic: ['⚔️', '🗡️', '🏹', '🔱'],
    legendary: ['⚔️', '🗡️', '🔱', '⚡'],
    mythic: ['⚔️', '🗡️', '🔱', '⚡', '🌌'],
  },
  armor: {
    common: ['🛡️'],
    uncommon: ['🛡️'],
    rare: ['🛡️'],
    epic: ['🛡️', '⚜️'],
    legendary: ['🛡️', '⚜️', '💎'],
    mythic: ['🛡️', '⚜️', '💎', '🌌'],
  },
  helmet: {
    common: ['⛑️'],
    uncommon: ['⛑️'],
    rare: ['⛑️', '👑'],
    epic: ['⛑️', '👑'],
    legendary: ['👑', '💎'],
    mythic: ['👑', '💎', '🌌'],
  },
  boots: {
    common: ['👢'],
    uncommon: ['👢'],
    rare: ['👢'],
    epic: ['👢', '⚡'],
    legendary: ['👢', '⚡', '🌟'],
    mythic: ['👢', '⚡', '🌟', '🌌'],
  },
  accessory: {
    common: ['💍', '🔮'],
    uncommon: ['💍', '🔮'],
    rare: ['💍', '🔮', '📿'],
    epic: ['💍', '🔮', '📿', '⭐'],
    legendary: ['💍', '🔮', '📿', '⭐', '💎'],
    mythic: ['💍', '🔮', '📿', '⭐', '💎', '🌌'],
  },
};
